/**
 * Mocked conversation list data for preview/mocked flows.
 * Used when in mocked mode to show pre-populated conversation lists.
 */

import type { ListTicketDetail } from '@/common/api/models.pleased';

const commonConversationFields: Partial<ListTicketDetail> = {
  requesterName: 'John Doe',
  requesterPhone: '1234567890',
  requesterEmail: 'john.doe@example.com',
  agentName: 'Jane Smith',
  agentEmail: 'jane.smith@example.com',
  recipientAddress: null,
  receivedAddress: null,
  requesterId: 12345,
  groupId: null,
  emailCCIds: null,
  slaTimestamp: null,
  brandId: 1,
  categoryId: null,
};

const OPEN_CONVERSATIONS_MOCK: ListTicketDetail[] = [
  {
    ...commonConversationFields,
    createdAt: new Date(Date.now()),
    updatedAt: new Date(Date.now()),
    updatedBy: 1,
    id: 456,
    subject: 'Order Inquiry',
    emailSubject: 'Order Inquiry - Ticket #456',
    priority: 'MEDIUM',
    status: 'NEW',
    assigneeId: 2,
    sourceId: 3,
    rating: null,
    lastMessagePreview: 'You: Thanks, when will it ship?',
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-22T09:15:00Z'),
    updatedAt: new Date('2024-01-23T10:20:00Z'),
    updatedBy: 1,
    id: 445,
    subject: 'Account Settings Question',
    emailSubject: 'Account Settings Question - Ticket #445',
    priority: 'LOW',
    status: 'PENDING',
    assigneeId: 3,
    sourceId: 1,
    rating: null,
    lastMessagePreview: 'Support: I can help update your email on file.',
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-21T16:45:00Z'),
    updatedAt: new Date('2024-01-22T11:00:00Z'),
    updatedBy: 2,
    id: 432,
    subject: 'Payment Processing Issue',
    emailSubject: 'Payment Processing Issue - Ticket #432',
    priority: 'HIGH',
    status: 'OPEN',
    assigneeId: 1,
    sourceId: 2,
    rating: null,
    lastMessagePreview: 'You: The charge still shows as pending.',
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-20T08:00:00Z'),
    updatedAt: new Date('2024-01-21T14:00:00Z'),
    updatedBy: 1,
    id: 420,
    subject: 'Refund Request',
    emailSubject: 'Refund Request - Ticket #420',
    priority: 'HIGH',
    status: 'OPEN',
    assigneeId: 2,
    sourceId: 1,
    rating: null,
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-19T11:30:00Z'),
    updatedAt: new Date('2024-01-20T09:00:00Z'),
    updatedBy: 2,
    id: 418,
    subject: 'Product Availability',
    emailSubject: 'Product Availability - Ticket #418',
    priority: 'LOW',
    status: 'PENDING',
    assigneeId: 3,
    sourceId: 2,
    rating: null,
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-18T15:20:00Z'),
    updatedAt: new Date('2024-01-19T10:15:00Z'),
    updatedBy: 1,
    id: 410,
    subject: 'Delivery Tracking',
    emailSubject: 'Delivery Tracking - Ticket #410',
    priority: 'MEDIUM',
    status: 'NEW',
    assigneeId: 1,
    sourceId: 3,
    rating: null,
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-17T09:45:00Z'),
    updatedAt: new Date('2024-01-18T16:30:00Z'),
    updatedBy: 2,
    id: 405,
    subject: 'Billing Question',
    emailSubject: 'Billing Question - Ticket #405',
    priority: 'MEDIUM',
    status: 'OPEN',
    assigneeId: 2,
    sourceId: 1,
    rating: null,
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-16T13:00:00Z'),
    updatedAt: new Date('2024-01-17T11:00:00Z'),
    updatedBy: 1,
    id: 398,
    subject: 'Technical Support',
    emailSubject: 'Technical Support - Ticket #398',
    priority: 'HIGH',
    status: 'PENDING',
    assigneeId: 3,
    sourceId: 2,
    rating: null,
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-15T10:30:00Z'),
    updatedAt: new Date('2024-01-16T14:45:00Z'),
    updatedBy: 2,
    id: 392,
    subject: 'Subscription Change',
    emailSubject: 'Subscription Change - Ticket #392',
    priority: 'LOW',
    status: 'NEW',
    assigneeId: 1,
    sourceId: 3,
    rating: null,
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-14T16:00:00Z'),
    updatedAt: new Date('2024-01-15T09:20:00Z'),
    updatedBy: 1,
    id: 385,
    subject: 'Account Merge Request',
    emailSubject: 'Account Merge Request - Ticket #385',
    priority: 'MEDIUM',
    status: 'OPEN',
    assigneeId: 2,
    sourceId: 1,
    rating: null,
  } as ListTicketDetail,
];

/**
 * Create mocked open conversations list (paginated: 10 items, total 10)
 * This simulates a list of active/open conversations for pagination UI
 */
export const createMockedOpenConversations = (): { data: ListTicketDetail[]; total: number } => ({
  data: [...OPEN_CONVERSATIONS_MOCK],
  total: OPEN_CONVERSATIONS_MOCK.length,
});

const CLOSED_CONVERSATIONS_MOCK: ListTicketDetail[] = [
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-20T10:00:00Z'),
    updatedAt: new Date('2024-01-20T10:30:00Z'),
    updatedBy: 2,
    id: 123,
    subject: 'Password Reset Issue',
    emailSubject: 'Password Reset Issue - Ticket #123',
    priority: 'MEDIUM',
    status: 'RESOLVED',
    assigneeId: 2,
    sourceId: 3,
    rating: 5,
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-18T14:20:00Z'),
    updatedAt: new Date('2024-01-18T15:45:00Z'),
    updatedBy: 1,
    id: 115,
    subject: 'Shipping Delay Inquiry',
    emailSubject: 'Shipping Delay Inquiry - Ticket #115',
    priority: 'LOW',
    status: 'CLOSED',
    assigneeId: 1,
    sourceId: 1,
    rating: 4,
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-17T09:00:00Z'),
    updatedAt: new Date('2024-01-17T12:00:00Z'),
    updatedBy: 2,
    id: 108,
    subject: 'Return Request',
    emailSubject: 'Return Request - Ticket #108',
    priority: 'MEDIUM',
    status: 'RESOLVED',
    assigneeId: 1,
    sourceId: 2,
    rating: 5,
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-16T11:15:00Z'),
    updatedAt: new Date('2024-01-16T14:30:00Z'),
    updatedBy: 1,
    id: 102,
    subject: 'Login Issue',
    emailSubject: 'Login Issue - Ticket #102',
    priority: 'HIGH',
    status: 'CLOSED',
    assigneeId: 3,
    sourceId: 1,
    rating: 4,
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-15T14:00:00Z'),
    updatedAt: new Date('2024-01-15T16:45:00Z'),
    updatedBy: 2,
    id: 98,
    subject: 'Pricing Inquiry',
    emailSubject: 'Pricing Inquiry - Ticket #98',
    priority: 'LOW',
    status: 'RESOLVED',
    assigneeId: 2,
    sourceId: 3,
    rating: 5,
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-14T08:30:00Z'),
    updatedAt: new Date('2024-01-14T10:00:00Z'),
    updatedBy: 1,
    id: 95,
    subject: 'Feature Request',
    emailSubject: 'Feature Request - Ticket #95',
    priority: 'LOW',
    status: 'CLOSED',
    assigneeId: 1,
    sourceId: 2,
    rating: 3,
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-13T16:20:00Z'),
    updatedAt: new Date('2024-01-13T17:00:00Z'),
    updatedBy: 2,
    id: 91,
    subject: 'API Integration Help',
    emailSubject: 'API Integration Help - Ticket #91',
    priority: 'MEDIUM',
    status: 'RESOLVED',
    assigneeId: 3,
    sourceId: 1,
    rating: 5,
  } as ListTicketDetail,
  {
    ...commonConversationFields,
    createdAt: new Date('2024-01-12T10:45:00Z'),
    updatedAt: new Date('2024-01-12T12:30:00Z'),
    updatedBy: 1,
    id: 87,
    subject: 'Documentation Request',
    emailSubject: 'Documentation Request - Ticket #87',
    priority: 'LOW',
    status: 'CLOSED',
    assigneeId: 2,
    sourceId: 3,
    rating: 4,
  } as ListTicketDetail,
];

/**
 * Create mocked closed/resolved conversations list (paginated: 8 items, total 8)
 * This simulates a list of resolved or closed conversations for pagination UI
 */
export const createMockedClosedConversations = (): { data: ListTicketDetail[]; total: number } => ({
  data: [...CLOSED_CONVERSATIONS_MOCK],
  total: CLOSED_CONVERSATIONS_MOCK.length,
});
